import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Geofence } from "@/hooks/useGeolocationSettings";
import { Loader2, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface GeofenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  geofence?: Geofence;
  defaultRadius: number;
  onSave: (data: Omit<Geofence, 'id' | 'company_id' | 'created_at' | 'updated_at'>) => Promise<void>;
}

export function GeofenceDialog({ open, onOpenChange, geofence, defaultRadius, onSave }: GeofenceDialogProps) {
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState(defaultRadius.toString());
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const autocompleteRef = useRef<any>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

  // Load Google Maps script
  useEffect(() => {
    if (!open || mapLoaded) return;

    const loadGoogleMapsScript = async () => {
      // Check if script already exists
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        setMapLoaded(true);
        return;
      }

      try {
        // Fetch API key from edge function
        const { data, error } = await supabase.functions.invoke('get-maps-api-key');
        
        if (error) {
          console.error('Error fetching Google Maps API key:', error);
          return;
        }

        const apiKey = data?.apiKey;
        if (!apiKey) {
          console.error('Google Maps API key not found');
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
        script.async = true;
        script.defer = true;
        script.onload = () => setMapLoaded(true);
        document.head.appendChild(script);
      } catch (err) {
        console.error('Failed to load Google Maps:', err);
      }
    };

    loadGoogleMapsScript();
  }, [open, mapLoaded]);

  // Initialize map and autocomplete when dialog opens
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !addressInputRef.current || !open) return;
    if (!(window as any).google?.maps) return;

    const google = (window as any).google;

    // Default center (will be updated when location is set)
    const defaultCenter = { lat: 40.7128, lng: -74.0060 };
    const center = latitude && longitude 
      ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
      : defaultCenter;

    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      // Initialize marker
      markerRef.current = new google.maps.Marker({
        map: mapInstanceRef.current,
        position: center,
        draggable: true,
        title: "Drag to adjust location",
      });

      // Initialize circle
      circleRef.current = new google.maps.Circle({
        map: mapInstanceRef.current,
        center,
        radius: parseInt(radius, 10) || defaultRadius,
        fillColor: "#4F46E5",
        fillOpacity: 0.2,
        strokeColor: "#4F46E5",
        strokeWeight: 2,
      });

      // Handle marker drag
      markerRef.current.addListener("dragend", () => {
        const pos = markerRef.current?.getPosition();
        if (pos) {
          setLatitude(pos.lat().toFixed(6));
          setLongitude(pos.lng().toFixed(6));
          circleRef.current?.setCenter(pos);
        }
      });

      // Initialize autocomplete
      autocompleteRef.current = new google.maps.places.Autocomplete(addressInputRef.current, {
        fields: ["formatted_address", "geometry", "name"],
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current?.getPlace();
        if (place?.geometry?.location) {
          const newPos = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          
          setAddress(place.formatted_address || place.name || "");
          setLatitude(newPos.lat.toFixed(6));
          setLongitude(newPos.lng.toFixed(6));
          
          mapInstanceRef.current?.setCenter(newPos);
          markerRef.current?.setPosition(newPos);
          circleRef.current?.setCenter(newPos);
        }
      });
    }
  }, [mapLoaded, open, defaultRadius]);

  // Update map when geofence data changes
  useEffect(() => {
    if (geofence) {
      setLabel(geofence.label);
      setAddress(geofence.address);
      setLatitude(geofence.latitude.toString());
      setLongitude(geofence.longitude.toString());
      setRadius(geofence.radius_meters.toString());
      setIsActive(geofence.is_active);
    } else {
      setLabel("");
      setAddress("");
      setLatitude("");
      setLongitude("");
      setRadius(defaultRadius.toString());
      setIsActive(true);
    }
  }, [geofence, defaultRadius]);

  // Update map position when coordinates change
  useEffect(() => {
    if (!mapInstanceRef.current || !latitude || !longitude) return;

    const newPos = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
    mapInstanceRef.current.setCenter(newPos);
    markerRef.current?.setPosition(newPos);
    circleRef.current?.setCenter(newPos);
  }, [latitude, longitude]);

  // Update circle radius when radius changes
  useEffect(() => {
    if (!circleRef.current) return;
    const radiusNum = parseInt(radius, 10);
    if (!isNaN(radiusNum)) {
      circleRef.current.setRadius(radiusNum);
    }
  }, [radius]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSave({
        label,
        address,
        place_id: null,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        radius_meters: parseInt(radius, 10),
        is_active: isActive,
        sort_order: geofence?.sort_order ?? 0,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = label && address && latitude && longitude && radius;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{geofence ? 'Edit Location' : 'Add Location'}</DialogTitle>
            <DialogDescription>
              Configure a geofence for employee time tracking
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="label">Location Name</Label>
              <Input
                id="label"
                placeholder="e.g., Main Office"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={addressInputRef}
                  id="address"
                  placeholder="Search for a location..."
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="pl-9"
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Start typing to search or select a location on the map
              </p>
            </div>
            
            {/* Map Preview */}
            {mapLoaded && (
              <div className="space-y-2">
                <Label>Map Preview</Label>
                <div 
                  ref={mapRef} 
                  className="w-full h-[300px] rounded-md border"
                />
                <p className="text-xs text-muted-foreground">
                  Drag the marker to fine-tune the location. The blue circle shows the geofence radius.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  placeholder="40.7128"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  placeholder="-74.0060"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="radius">Radius (meters)</Label>
              <Input
                id="radius"
                type="number"
                min="10"
                max="5000"
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Employees can clock in/out within this distance from the location
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is-active">Active</Label>
                <p className="text-sm text-muted-foreground">
                  Only active locations are used for validation
                </p>
              </div>
              <Switch
                id="is-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {geofence ? 'Update' : 'Add'} Location
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
